import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Legal.css';

/**
 * Dokumenty prawne (regulamin, polityka prywatności) trzymamy jako pliki .txt
 * w public/legal — poza bundlem JS, więc podmiana treści to podmiana pliku,
 * bez przebudowy frontendu. Strona jest publiczna: linkuje do niej zarówno menu
 * konta, jak i formularz aktywacji zaproszenia (użytkownik jeszcze niezalogowany).
 */
type LegalDocProps = { file: string; title: string };

type Block =
  | { kind: 'title'; text: string }
  | { kind: 'heading'; marker?: string; text: string }
  | { kind: 'item'; text: string }
  | { kind: 'subitem'; text: string }
  | { kind: 'continuation'; text: string }
  | { kind: 'text'; text: string };

const SECTION_MARKER = /^§\s*\d+\.?$/;              // „§ 4." w osobnej linii, tytuł w następnej
const ROMAN_HEADING = /^[IVXLC]+\.\s+\S/;           // „III. DANE PRZETWARZANE…"
const NUMBERED_ITEM = /^\d+\.\s/;                   // „1. Treść ustępu"
const LETTER_SUBITEM = /^\d+\)\s|^[a-z]\)\s/;       // „2) podpunkt" (w pliku wcięty spacjami)

function parse(raw: string): Block[] {
  const lines = raw.replace(/\r\n/g, '\n').split('\n');
  const blocks: Block[] = [];
  let titleTaken = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();
    if (!trimmed) continue;

    if (!titleTaken) {
      blocks.push({ kind: 'title', text: trimmed });
      titleTaken = true;
      continue;
    }

    // „§ N." + tytuł sekcji z kolejnej niepustej linii scalamy w jeden nagłówek.
    if (SECTION_MARKER.test(trimmed)) {
      let j = i + 1;
      while (j < lines.length && !lines[j].trim()) j++;
      const heading = j < lines.length ? lines[j].trim() : '';
      blocks.push({ kind: 'heading', marker: trimmed, text: heading });
      i = j;
      continue;
    }

    if (ROMAN_HEADING.test(trimmed)) {
      blocks.push({ kind: 'heading', text: trimmed });
      continue;
    }

    // Wcięcie w pliku źródłowym oznacza podpunkt niższego rzędu.
    const indented = /^\s+/.test(line);
    if (LETTER_SUBITEM.test(trimmed)) {
      blocks.push({ kind: indented ? 'subitem' : 'item', text: trimmed });
      continue;
    }

    if (NUMBERED_ITEM.test(trimmed)) {
      blocks.push({ kind: 'item', text: trimmed });
      continue;
    }

    // Wcięta linia bez numeracji to domknięcie wyliczenia („…, podając uzasadnienie
    // swojej decyzji."). Trzymamy ją przy liście, zamiast wyrzucać na margines.
    if (indented) {
      blocks.push({ kind: 'continuation', text: trimmed });
      continue;
    }

    blocks.push({ kind: 'text', text: trimmed });
  }

  return blocks;
}

const LegalDoc: React.FC<LegalDocProps> = ({ file, title }) => {
  const navigate = useNavigate();
  const [blocks, setBlocks] = useState<Block[] | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = `${title} — Maszynownik`;
    let cancelled = false;
    fetch(`${process.env.PUBLIC_URL || ''}/legal/${file}`)
      .then((res) => {
        if (!res.ok) throw new Error(String(res.status));
        return res.text();
      })
      .then((text) => {
        if (!cancelled) setBlocks(parse(text));
      })
      .catch(() => {
        if (!cancelled) setError('Nie udało się wczytać dokumentu. Odśwież stronę lub spróbuj później.');
      });
    return () => {
      cancelled = true;
    };
  }, [file, title]);

  return (
    <div className="legal-page">
      <div className="legal-container">
        <div className="legal-topbar">
          <button type="button" className="legal-back" onClick={() => navigate(-1)}>
            ← Wróć
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => window.print()}>
            Drukuj / zapisz PDF
          </button>
        </div>

        <div className="legal-card">
          {error && <div className="auth-error">{error}</div>}
          {!blocks && !error && <p className="legal-text">Ładowanie…</p>}

          {blocks?.map((b, i) => {
            switch (b.kind) {
              case 'title':
                return (
                  <React.Fragment key={i}>
                    <h1 className="legal-title">{b.text}</h1>
                    <p className="legal-meta">Maszynownik — dokument obowiązujący w aplikacji.</p>
                  </React.Fragment>
                );
              case 'heading':
                return (
                  <h2 className="legal-heading" key={i}>
                    {b.marker && <span className="legal-heading-marker">{b.marker}</span>}
                    {b.text}
                  </h2>
                );
              case 'item':
                return <p className="legal-item" key={i}>{b.text}</p>;
              case 'subitem':
                return <p className="legal-subitem" key={i}>{b.text}</p>;
              case 'continuation':
                return <p className="legal-continuation" key={i}>{b.text}</p>;
              default:
                return <p className="legal-text" key={i}>{b.text}</p>;
            }
          })}
        </div>
      </div>
    </div>
  );
};

export const Terms: React.FC = () => <LegalDoc file="regulamin.txt" title="Regulamin" />;

export const PrivacyPolicy: React.FC = () => (
  <LegalDoc file="polityka-prywatnosci.txt" title="Polityka prywatności" />
);

export default LegalDoc;
